import { Box, Card, Typography } from '@mui/material'
import React, { useState } from 'react'
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, Label, SectorProps } from 'recharts'
import { PolarViewBox } from 'recharts/types/util/types'
import { makeStyles } from 'tss-react/mui'
import ShopCustomLegend from './ShopPieChartCustomLegend'
import ShopPieChartCustomTooltip from './ShopPieChartCustomTooltip'
import ShopPieChartActiveShape from './ShopPieChartActiveShape'
import ShopPieChartCustomLabel from './ShopPieChartCustomLabel'

export type PieValueType = 'number' | 'currency'

interface ShopPieChartProps {
  valueType: PieValueType
  filledValue: number
  totalValue: number
  filledValueLabel: string
  totalValueLabel: string
  tooltipFilledLabel: string
  tooltipUnfilledLabel: string
  filledColor: string
  totalColor: string
  noDataValueLabel: string
}

const useStyles = makeStyles()(theme => ({
  wrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: 'min-content',
  },
  noDataContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}))

/*
 * This Component renders a Pie Chart to the shop promotion codes overview section.
 */
const ShopPieChart: React.FC<ShopPieChartProps> = ({
  valueType,
  filledValue,
  totalValue,
  filledValueLabel,
  totalValueLabel,
  tooltipFilledLabel,
  tooltipUnfilledLabel,
  noDataValueLabel,
  filledColor,
  totalColor,
}) => {
  const { classes } = useStyles()
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const percentage = ((filledValue / totalValue) * 100).toFixed(1)

  const onPieEnter = (_: number, index: number): void => {
    setActiveIndex(index)
  }

  const onPieLeave = (): void => {
    setActiveIndex(-1)
  }

  const pieData = [
    { name: filledValueLabel, value: filledValue },
    { name: totalValueLabel, value: totalValue - filledValue },
  ]

  return (
    <Card className={classes.wrapper}>
      <ResponsiveContainer width={350} height={200}>
        {totalValue !== 0 ? (
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              innerRadius="50%"
              outerRadius="80%"
              fill="#8884d8"
              dataKey="value"
              activeIndex={activeIndex !== -1 ? activeIndex : undefined}
              activeShape={(props: SectorProps) => <ShopPieChartActiveShape selector={props} borderColor={filledColor} />}
              onMouseEnter={onPieEnter}
              onMouseLeave={onPieLeave}
            >
              <Cell key="cell-filled" fill={filledColor} />
              <Cell key="cell-unfilled" fill={totalColor} />
              <Label
                position="center"
                content={props => (
                  <ShopPieChartCustomLabel viewBox={props.viewBox as PolarViewBox} filledColor={filledColor} percentage={percentage} />
                )}
              />
            </Pie>

            <Tooltip
              content={
                <ShopPieChartCustomTooltip
                  valueType={valueType}
                  filledValue={filledValue}
                  tooltipFilledLabel={tooltipFilledLabel}
                  tooltipUnfilledLabel={tooltipUnfilledLabel}
                  filledColor={filledColor}
                />
              }
            />
            <Legend
              layout="vertical"
              verticalAlign="middle"
              align="left"
              content={
                <ShopCustomLegend
                  valueType={valueType}
                  totalValue={totalValue}
                  filledValue={filledValue}
                  totalValueLabel={totalValueLabel}
                  filledValueLabel={filledValueLabel}
                  filledColor={filledColor}
                />
              }
            />
          </PieChart>
        ) : (
          <Box className={classes.noDataContainer}>
            <Typography variant="subtitle2">{noDataValueLabel}</Typography>
          </Box>
        )}
      </ResponsiveContainer>
    </Card>
  )
}

export default ShopPieChart
